import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Td,
  Text,
  Tr,
  useDisclosure,
  useToast,
  VStack
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, DownloadIcon, EditIcon, SearchIcon } from "@chakra-ui/icons";
import { FaSitemap, FaUserFriends } from "react-icons/fa";
import { addNeedComment, deleteNeed, downloadNeedJobDescription, getNeed, getNeedComments } from "../../../api/needsApi";
import { createMatch, deleteMatch, generateMatchesForNeed, listMatches } from "../../../api/matchesApi";
import { listConsultants } from "../../../api/consultantsApi";
import { listMentionableUsers } from "../../../api/authApi";
import ThreadedComments from "../../../components/Portal/ThreadedComments";
import ConfirmDialog from "../../../components/Portal/ConfirmDialog";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { PLATFORM_ROLES, BU_ROLES } from "../../../constants/roles";
import NeedFormModal from "./NeedFormModal";
import MatchDetailModal from "./MatchDetailModal";
import { useTranslation } from "react-i18next";
import {
  NEED_STATUS_LABELS,
  NEED_STATUS_LABEL_KEYS,
  NEED_STATUS_COLORS,
  MATCH_ASSIGNMENT_STATUS_LABELS,
  MATCH_ASSIGNMENT_STATUS_LABEL_KEYS,
  getStatusLabel
} from "../../../constants/statuses";
import { ACQUISITION_CHANNEL_LABELS, CONTRACT_TYPE_LABELS } from "../../../constants/needs";
import {
  Panel,
  DataTable,
  StatusBadge,
  MiniIconButton,
  EmptyState,
  KpiStrip,
  KpiCard,
  FilterSearch,
  FieldLabel
} from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX
} from "../../../components/Portal/portalModalChrome";

// Field value text inside detail Panels.
const FieldValue = ({ children, ...rest }) => (
  <Text fontSize="13px" fontWeight="600" color="portal.text" sx={{ marginBottom: "0 !important" }} {...rest}>
    {children}
  </Text>
);

const NeedDetailModal = ({ isOpen, onClose, needId, shouldNavigateOnClose = true }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { effectiveRole, selectedBusinessUnitId, isLoaded: isBUContextLoaded } =
    useBusinessUnit();

  const [loading, setLoading] = useState(true);
  const [need, setNeed] = useState(null);

  // Comments
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [mentionableUsers, setMentionableUsers] = useState([]);

  // Matches (Need × Consultant)
  const [generatingMatches, setGeneratingMatches] = useState(false);

  // Consultant selection for targeted generation (reused from Matches page)
  const [isSelectConsultantsOpen, setIsSelectConsultantsOpen] = useState(false);
  const [generationMode, setGenerationMode] = useState("auto"); // "auto" | "manual"
  const [consultantsLoading, setConsultantsLoading] = useState(false);
  const [consultants, setConsultants] = useState([]);
  const [consultantsPage, setConsultantsPage] = useState(1);
  const [consultantsPageSize] = useState(20);
  const [consultantsTotalItems, setConsultantsTotalItems] = useState(0);
  const [consultantsSearch, setConsultantsSearch] = useState("");
  const [selectedConsultantIds, setSelectedConsultantIds] = useState([]);

  // Manual pairings (Need × Consultant) - add existing consultants as matches
  const [isAddConsultantsOpen, setIsAddConsultantsOpen] = useState(false);
  const [pairingConsultantsLoading, setPairingConsultantsLoading] = useState(false);
  const [pairingConsultants, setPairingConsultants] = useState([]);
  const [pairingConsultantsPage, setPairingConsultantsPage] = useState(1);
  const [pairingConsultantsPageSize] = useState(20);
  const [pairingConsultantsTotalItems, setPairingConsultantsTotalItems] = useState(0);
  const [pairingConsultantsSearch, setPairingConsultantsSearch] = useState("");
  const [selectedPairingConsultantIds, setSelectedPairingConsultantIds] = useState([]);
  const [creatingPairings, setCreatingPairings] = useState(false);

  const editModal = useDisclosure();
  const deleteConfirmDialog = useDisclosure();
  const [editingItem, setEditingItem] = useState(null);
  const matchDetailModal = useDisclosure();
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedNeed, setSelectedNeed] = useState(null);

  const canDelete = useMemo(() => {
    return (
      effectiveRole === PLATFORM_ROLES.COMPANY_ADMIN ||
      effectiveRole === BU_ROLES.ADMIN ||
      effectiveRole === BU_ROLES.MANAGER
    );
  }, [effectiveRole]);

  const fetchNeed = useCallback(async () => {
    if (!needId) return;
    setLoading(true);
    try {
      // Note: the single-need endpoint may not include matches even when requested.
      // To mirror the Matches page behavior, we fetch matches explicitly and attach them.
      const data = await getNeed(needId, {
        params: { includeMatches: true },
        buId: selectedBusinessUnitId || undefined
      });
      let matchesRes = null;
      try {
        matchesRes = await listMatches(
          { needId, page: 1, pageSize: 1000 },
          { buId: selectedBusinessUnitId || undefined }
        );
      } catch {
        matchesRes = null;
      }
      const items = Array.isArray(matchesRes?.items) ? matchesRes.items : null;
      setNeed(items ? { ...(data || {}), matches: items } : data);
    } catch (e) {
      toast({ title: t("pilotNeeds.toasts.loadDetailFailed"), status: "error" });
      setNeed(null);
    } finally {
      setLoading(false);
    }
  }, [needId, selectedBusinessUnitId, toast, t]);

  const fetchComments = useCallback(async () => {
    if (!needId) return;
    setLoadingComments(true);
    try {
      const res = await getNeedComments(needId, {
        buId: selectedBusinessUnitId || undefined
      });
      setComments(res || []);
    } catch (e) {
      // keep silent (consultant implementation also logs only)
      // console.error("Failed to load need comments", e);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, [needId, selectedBusinessUnitId]);

  const fetchMentionableUsers = useCallback(async () => {
    try {
      const res = await listMentionableUsers({
        buId: selectedBusinessUnitId || undefined
      });
      setMentionableUsers(res || []);
    } catch (e) {
      console.error("Failed to load mentionable users", e);
    }
  }, [selectedBusinessUnitId]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isBUContextLoaded) return;
    fetchNeed();
    fetchComments();
    fetchMentionableUsers();
  }, [isOpen, isBUContextLoaded, fetchNeed, fetchComments, fetchMentionableUsers]);

  useEffect(() => {
    if (isOpen) return;
    // Reset matches tab state when closing
    setIsSelectConsultantsOpen(false);
    setGenerationMode("auto");
    setConsultants([]);
    setConsultantsLoading(false);
    setConsultantsPage(1);
    setConsultantsSearch("");
    setConsultantsTotalItems(0);
    setSelectedConsultantIds([]);
    setGeneratingMatches(false);

    // Reset pairing tab state when closing
    setIsAddConsultantsOpen(false);
    setPairingConsultants([]);
    setPairingConsultantsLoading(false);
    setPairingConsultantsPage(1);
    setPairingConsultantsSearch("");
    setPairingConsultantsTotalItems(0);
    setSelectedPairingConsultantIds([]);
    setCreatingPairings(false);
  }, [isOpen]);

  const handleDelete = async () => {
    try {
      await deleteNeed(needId, { buId: selectedBusinessUnitId || undefined });
      toast({ title: t("pilotNeeds.toasts.deleteSuccess"), status: "success" });
      onClose?.();
      if (shouldNavigateOnClose) {
        navigate("/portal/asap-pilot/needs");
      }
    } catch (e) {
      toast({
        title: t("pilotNeeds.toasts.deleteFailedTitle"),
        description:
          e?.response?.data?.message || t("pilotNeeds.toasts.deleteFailedDescription"),
        status: "error"
      });
    }
  };

  const handleEditSuccess = () => {
    fetchNeed();
  };

  const handleDownloadJobDescription = async () => {
    if (!needId) return;
    try {
      const response = await downloadNeedJobDescription(needId, {
        buId: selectedBusinessUnitId || undefined
      });
      const blob = response.data;
      const dispo = response.headers["content-disposition"] || "";
      const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(dispo);
      const serverFilename = match ? match[1].replace(/["']/g, "") : null;
      const fallbackName = `${need?.title || "job_description"}`;
      const finalName = serverFilename || fallbackName;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", finalName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to download job description", e);
      toast({
        title: t("asapCV.toasts.downloadFailedTitle"),
        description: t("asapCV.toasts.downloadErrors.generic"),
        status: "error"
      });
    }
  };

  const matches = useMemo(() => {
    return Array.isArray(need?.matches) ? need.matches : [];
  }, [need]);

  const sortedMatches = useMemo(() => {
    return [...matches].sort((a, b) => (Number(b?.score) || 0) - (Number(a?.score) || 0));
  }, [matches]);

  const openSelectConsultants = () => {
    setGenerationMode("auto");
    setSelectedConsultantIds([]);
    setConsultantsSearch("");
    setConsultantsPage(1);
    setIsSelectConsultantsOpen(true);
  };

  const closeSelectConsultants = () => {
    setIsSelectConsultantsOpen(false);
    setSelectedConsultantIds([]);
  };

  const fetchConsultantsForSelection = useCallback(async () => {
    if (!isBUContextLoaded || !isSelectConsultantsOpen || generationMode !== "manual") return;
    setConsultantsLoading(true);
    try {
      const params = {
        search: consultantsSearch || undefined,
        page: consultantsPage,
        pageSize: consultantsPageSize
      };
      const res = await listConsultants(params, { buId: selectedBusinessUnitId || undefined });
      setConsultants(res.items || []);
      setConsultantsTotalItems(res.totalItems || 0);
    } catch (e) {
      toast({
        title: t("pilotMatches.toasts.loadMatchesFailedTitle"),
        description: e?.response?.data?.message || t("pilotMatches.toasts.loadMatchesFailedDescription"),
        status: "error"
      });
      setConsultants([]);
      setConsultantsTotalItems(0);
    } finally {
      setConsultantsLoading(false);
    }
  }, [
    consultantsPage,
    consultantsPageSize,
    consultantsSearch,
    generationMode,
    isBUContextLoaded,
    isSelectConsultantsOpen,
    selectedBusinessUnitId,
    t,
    toast
  ]);

  useEffect(() => {
    if (!isSelectConsultantsOpen) return;
    fetchConsultantsForSelection();
  }, [fetchConsultantsForSelection, isSelectConsultantsOpen]);

  const toggleConsultantSelection = (id) => {
    setSelectedConsultantIds((prev) => {
      if (prev.includes(id)) return prev.filter((cId) => cId !== id);
      if (prev.length >= 5) {
        toast({
          title: t("pilotMatches.toasts.maxConsultantsSelected", { max: 5 }),
          status: "warning"
        });
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleConfirmGenerate = async () => {
    if (!needId) return;
    const isManual = generationMode === "manual";
    const consultantIds =
      isManual && selectedConsultantIds.length > 0 ? selectedConsultantIds : null;
    if (isManual && (!consultantIds || consultantIds.length === 0)) {
      return;
    }

    closeSelectConsultants();
    setGeneratingMatches(true);
    try {
      const items = await generateMatchesForNeed(
        needId,
        consultantIds ? { consultantIds } : null,
        { buId: selectedBusinessUnitId || undefined }
      );
      const normalizedItems = Array.isArray(items) ? items : [];
      setNeed((prev) => (prev ? { ...prev, matches: normalizedItems } : prev));
      toast({ title: t("pilotMatches.toasts.generateSuccess"), status: "success" });
    } catch (e) {
      toast({
        title: t("pilotMatches.toasts.generateFailedTitle"),
        description: e?.response?.data?.message || t("pilotMatches.toasts.generateFailedDescription"),
        status: "error"
      });
    } finally {
      setGeneratingMatches(false);
    }
  };

  const handleDeleteMatch = async (matchId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!canDelete) return;
    try {
      await deleteMatch(matchId, { buId: selectedBusinessUnitId || undefined });
      setNeed((prev) => {
        if (!prev) return prev;
        const current = Array.isArray(prev.matches) ? prev.matches : [];
        return { ...prev, matches: current.filter((m) => m.id !== matchId) };
      });
      toast({ title: t("pilotMatches.toasts.deleteSuccess"), status: "success" });
    } catch (err) {
      toast({
        title: t("pilotMatches.toasts.deleteFailedTitle"),
        description: err?.response?.data?.message || t("pilotMatches.toasts.deleteFailedDescription"),
        status: "error"
      });
    }
  };

  const openAddConsultants = () => {
    setPairingConsultantsSearch("");
    setPairingConsultantsPage(1);
    setSelectedPairingConsultantIds([]);
    setIsAddConsultantsOpen(true);
  };

  const closeAddConsultants = () => {
    setIsAddConsultantsOpen(false);
    setSelectedPairingConsultantIds([]);
  };

  const fetchConsultantsForPairing = useCallback(async () => {
    if (!isBUContextLoaded || !isAddConsultantsOpen) return;
    setPairingConsultantsLoading(true);
    try {
      const params = {
        search: pairingConsultantsSearch || undefined,
        page: pairingConsultantsPage,
        pageSize: pairingConsultantsPageSize
      };
      const res = await listConsultants(params, { buId: selectedBusinessUnitId || undefined });
      setPairingConsultants(res.items || []);
      setPairingConsultantsTotalItems(res.totalItems || 0);
    } catch (e) {
      toast({
        title: t("pilotNeeds.detail.pairings.toasts.loadConsultantsFailedTitle"),
        description:
          e?.response?.data?.message ||
          t("pilotNeeds.detail.pairings.toasts.loadConsultantsFailedDescription"),
        status: "error"
      });
      setPairingConsultants([]);
      setPairingConsultantsTotalItems(0);
    } finally {
      setPairingConsultantsLoading(false);
    }
  }, [
    isBUContextLoaded,
    isAddConsultantsOpen,
    pairingConsultantsSearch,
    pairingConsultantsPage,
    pairingConsultantsPageSize,
    selectedBusinessUnitId,
    t,
    toast
  ]);

  useEffect(() => {
    if (!isAddConsultantsOpen) return;
    fetchConsultantsForPairing();
  }, [fetchConsultantsForPairing, isAddConsultantsOpen]);

  const togglePairingConsultantSelection = (id) => {
    setSelectedPairingConsultantIds((prev) => {
      if (prev.includes(id)) return prev.filter((cId) => cId !== id);
      if (prev.length >= 5) {
        toast({
          title: t("pilotMatches.toasts.maxConsultantsSelected", { max: 5 }),
          status: "warning"
        });
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleConfirmAddConsultants = async () => {
    if (!needId) return;
    if (selectedPairingConsultantIds.length === 0) return;

    setCreatingPairings(true);
    try {
      const payloads = selectedPairingConsultantIds.map((consultantId) => ({
        needId,
        consultantId,
        source: "MANUAL"
      }));

      const results = await Promise.allSettled(
        payloads.map((payload) =>
          createMatch(payload, { buId: selectedBusinessUnitId || undefined })
        )
      );

      const successCount = results.filter((r) => r.status === "fulfilled").length;
      const firstError = results.find((r) => r.status === "rejected")?.reason;

      if (successCount > 0) {
        toast({
          title: t("pilotNeeds.detail.pairings.toasts.addSuccess", { count: successCount }),
          status: "success"
        });
        await fetchNeed();
      }

      if (firstError) {
        toast({
          title: t("pilotNeeds.detail.pairings.toasts.addFailedTitle"),
          description:
            firstError?.response?.data?.message ||
            t("pilotNeeds.detail.pairings.toasts.addFailedDescription"),
          status: "error"
        });
      }
    } finally {
      setCreatingPairings(false);
      closeAddConsultants();
    }
  };

  // Parse skills - support both camelCase (API response) and snake_case formats
  const parseSkills = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      } catch (_) {
        return [];
      }
    }
    return [];
  };

  const skillsTechnical = useMemo(
    () => parseSkills(need?.skillsTechnical || need?.skills_technical),
    [need]
  );
  const skillsFunctional = useMemo(
    () => parseSkills(need?.skillsFunctional || need?.skills_functional),
    [need]
  );
  const skillsBusiness = useMemo(
    () => parseSkills(need?.skillsBusiness || need?.skills_business),
    [need]
  );

  const parseMulti = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.filter(Boolean);
      } catch (_) {
        return [];
      }
    }
    return [];
  };

  const acquisitionChannels = useMemo(
    () => parseMulti(need?.acquisitionChannels || need?.acquisition_channels),
    [need]
  );

  const contractTypes = useMemo(
    () => parseMulti(need?.contractTypes || need?.contract_types),
    [need]
  );

  const safeClose = () => {
    onClose?.();
    if (shouldNavigateOnClose) {
      try {
        navigate("/portal/asap-pilot/needs");
      } catch {
        // ignore
      }
    }
  };

  const handleOpenMatchDetail = (match, matchNeed) => {
    if (!match?.id) return;
    setSelectedMatchId(match.id);
    setSelectedMatch(match);
    setSelectedNeed(matchNeed || null);
    matchDetailModal.onOpen();
  };

  const hasJobDescription = Boolean(
    need?.job_description_url || need?.jobDescriptionUrl || need?.jobDescription
  );

  const getConsultantDisplayName = (match) => {
    if (!match) return "-";
    if (match.consultantName) return match.consultantName;
    if (match.consultant_name) return match.consultant_name;
    const consultant = match.consultant;
    if (consultant?.name) return consultant.name;
    if (consultant?.fullName) return consultant.fullName;
    const first = consultant?.firstName || consultant?.first_name || "";
    const last = consultant?.lastName || consultant?.last_name || "";
    const combined = `${first} ${last}`.trim();
    return combined || "-";
  };

  // ---- Matches tab DataTable columns ----
  const matchColumns = [
    { key: "consultant", label: t("pilotMatches.detail.table.consultant"), width: "26%" },
    { key: "score", label: t("pilotMatches.detail.table.score"), width: "10%" },
    { key: "source", label: t("pilotMatches.detail.table.source"), width: "14%" },
    { key: "reasoning", label: t("pilotMatches.detail.table.reasoning") },
    { key: "actions", label: t("pilotMatches.detail.table.actions"), width: "70px", align: "right" }
  ];

  // ---- Pairings tab DataTable columns ----
  const pairingColumns = [
    { key: "consultant", label: t("pilotMatches.detail.table.consultant"), width: "26%" },
    {
      key: "assignmentStatus",
      label: t("pilotMatches.detail.missionTracking.fields.assignmentStatus"),
      width: "22%"
    },
    { key: "score", label: t("pilotMatches.detail.table.score"), width: "10%" },
    { key: "source", label: t("pilotMatches.detail.table.source"), width: "14%" },
    { key: "actions", label: t("pilotMatches.detail.table.actions"), width: "70px", align: "right" }
  ];

  const selectConsultantColumns = [
    { key: "check", label: "", width: "44px" },
    { key: "name", label: t("pilotMatches.detail.table.consultant") },
    { key: "role", label: t("pilotMatches.list.selectModal.roleColumn"), width: "32%" }
  ];

  const pairingSelectColumns = [
    { key: "check", label: "", width: "44px" },
    { key: "name", label: t("pilotMatches.detail.table.consultant") },
    { key: "role", label: t("pilotNeeds.detail.pairings.modal.roleColumn"), width: "32%" }
  ];

  const headerTitle =
    need?.title ||
    (loading ? t("pilotNeeds.detail.loading") : t("pilotNeeds.detail.notFound"));

  const matchesEmpty = (
    <EmptyState
      icon={<FaSitemap />}
      title={t("pilotMatches.detail.noMatches")}
      hint={t(
        "pilotNeeds.detail.matchesPanel.emptyHint",
        "Generate matches or add consultants to populate this need."
      )}
    />
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={safeClose} size="6xl">
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX} pr="56px">
            <Flex align="center" justify="space-between" gap="12px" flexWrap="wrap">
              <HStack spacing="12px" minW={0}>
                <Heading
                  as="h2"
                  fontSize="17px"
                  fontWeight={800}
                  letterSpacing="-0.02em"
                  color="portal.text"
                  isTruncated
                  sx={{ marginBottom: "0 !important" }}
                >
                  {headerTitle}
                </Heading>
                {need?.status && (
                  <StatusBadge
                    label={getStatusLabel(
                      t,
                      NEED_STATUS_LABEL_KEYS,
                      NEED_STATUS_LABELS,
                      need.status
                    )}
                    colorScheme={NEED_STATUS_COLORS[need.status] || "gray"}
                  />
                )}
              </HStack>
              <HStack spacing="8px" flexShrink={0}>
                {hasJobDescription && (
                  <Button
                    leftIcon={<DownloadIcon />}
                    variant="secondary"
                    size="sm"
                    onClick={handleDownloadJobDescription}
                  >
                    {t("pilotNeeds.detail.header.downloadJobDescription", {
                      defaultValue: "Download job description"
                    })}
                  </Button>
                )}
                <Button
                  leftIcon={<EditIcon />}
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setEditingItem(need);
                    editModal.onOpen();
                  }}
                  isDisabled={!need}
                >
                  {t("pilotNeeds.detail.header.edit")}
                </Button>
                {canDelete && (
                  <Button
                    leftIcon={<DeleteIcon />}
                    variant="secondary"
                    size="sm"
                    color="portal.danger"
                    borderColor="portal.danger"
                    _hover={{ bg: "danger.50", borderColor: "portal.danger" }}
                    onClick={deleteConfirmDialog.onOpen}
                    isDisabled={!need}
                  >
                    {t("pilotNeeds.detail.header.delete")}
                  </Button>
                )}
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody bg="portal.bg" py="14px">
            <Tabs
              variant="enclosed"
              colorScheme="blue"
              isFitted
              sx={{
                "& [role=tab]": {
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "portal.muted"
                },
                "& [role=tab][aria-selected=true]": {
                  color: "portal.primary",
                  borderColor: "portal.line",
                  borderBottomColor: "transparent"
                }
              }}
            >
              <TabList>
                <Tab>{t("pilotNeeds.detail.tabs.description")}</Tab>
                <Tab>{t("pilotNeeds.detail.tabs.matches")}</Tab>
                <Tab>{t("pilotNeeds.detail.tabs.pairings")}</Tab>
                <Tab>{t("pilotNeeds.detail.tabs.comments")}</Tab>
              </TabList>
              <TabPanels>
                {/* Tab 1: Description */}
                <TabPanel px={0} pt="14px">
                  {loading ? (
                    <Panel title={t("pilotNeeds.detail.sections.description")}>
                      <Flex align="center" justify="center" py="40px">
                        <Spinner color="portal.primary" />
                      </Flex>
                    </Panel>
                  ) : !need ? (
                    <Panel>
                      <EmptyState title={t("pilotNeeds.detail.notFound")} />
                    </Panel>
                  ) : (
                    <VStack align="stretch" spacing="10px">
                      {(need.daysOpen != null || sortedMatches.length > 0) && (
                        <KpiStrip columns={{ base: 1, md: 2 }}>
                          {need.daysOpen != null && (
                            <KpiCard
                              label={t("pilotNeeds.detail.sections.daysOpen", {
                                defaultValue: "Days Open"
                              })}
                              value={need.daysOpen}
                            />
                          )}
                          <KpiCard
                            label={t("pilotNeeds.detail.tabs.matches")}
                            value={sortedMatches.length}
                          />
                        </KpiStrip>
                      )}

                      <Panel title={t("pilotNeeds.detail.sections.description")}>
                        <Box p="12px">
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="16px" spacingY="12px">
                            <Box>
                              <FieldLabel>{t("pilotNeeds.detail.sections.client")}</FieldLabel>
                              <FieldValue>{need.clientName || "N/A"}</FieldValue>
                            </Box>
                            <Box>
                              <FieldLabel>{t("pilotNeeds.detail.sections.date")}</FieldLabel>
                              <FieldValue>{need.date || "N/A"}</FieldValue>
                            </Box>
                            <Box>
                              <FieldLabel>
                                {t("pilotNeeds.detail.sections.acquisitionChannels")}
                              </FieldLabel>
                              <FieldValue>
                                {acquisitionChannels.length === 0
                                  ? "N/A"
                                  : acquisitionChannels
                                      .map((v) => ACQUISITION_CHANNEL_LABELS[v] || v)
                                      .join(", ")}
                              </FieldValue>
                            </Box>
                            <Box>
                              <FieldLabel>{t("pilotNeeds.detail.sections.status")}</FieldLabel>
                              {need.status ? (
                                <StatusBadge
                                  label={getStatusLabel(
                                    t,
                                    NEED_STATUS_LABEL_KEYS,
                                    NEED_STATUS_LABELS,
                                    need.status
                                  )}
                                  colorScheme={NEED_STATUS_COLORS[need.status] || "gray"}
                                />
                              ) : (
                                <FieldValue>N/A</FieldValue>
                              )}
                            </Box>
                            <Box>
                              <FieldLabel>
                                {t("pilotNeeds.detail.sections.contractTypes")}
                              </FieldLabel>
                              <FieldValue>
                                {contractTypes.length === 0
                                  ? "N/A"
                                  : contractTypes
                                      .map((v) => CONTRACT_TYPE_LABELS[v] || v)
                                      .join(", ")}
                              </FieldValue>
                            </Box>
                            <Box>
                              <FieldLabel>{t("pilotNeeds.detail.sections.startDate")}</FieldLabel>
                              <FieldValue>
                                {need.asap
                                  ? t("pilotNeeds.detail.sections.asap")
                                  : need.startDate || need.start_date || "N/A"}
                              </FieldValue>
                            </Box>
                            <Box>
                              <FieldLabel>{t("pilotNeeds.detail.sections.endDate")}</FieldLabel>
                              <FieldValue>{need.endDate || need.end_date || "N/A"}</FieldValue>
                            </Box>
                            <Box>
                              <FieldLabel>{t("pilotNeeds.detail.sections.sales")}</FieldLabel>
                              <FieldValue>{need.salesName || "N/A"}</FieldValue>
                            </Box>
                            <Box>
                              <FieldLabel>
                                {t("pilotNeeds.detail.sections.recruiter", {
                                  defaultValue: "Recruiter(s)"
                                })}
                              </FieldLabel>
                              <FieldValue>
                                {Array.isArray(need.recruiterNames) && need.recruiterNames.length > 0
                                  ? need.recruiterNames.join(", ")
                                  : need.recruiterName || "N/A"}
                              </FieldValue>
                            </Box>
                            {need.clientContactName && (
                              <Box>
                                <FieldLabel>
                                  {t("pilotNeeds.detail.sections.contactPerson", {
                                    defaultValue: "Contact Person"
                                  })}
                                </FieldLabel>
                                <FieldValue>{need.clientContactName}</FieldValue>
                              </Box>
                            )}
                            {need.daysOpen != null && (
                              <Box>
                                <FieldLabel>
                                  {t("pilotNeeds.detail.sections.daysOpen", {
                                    defaultValue: "Days Open"
                                  })}
                                </FieldLabel>
                                <FieldValue>{need.daysOpen}</FieldValue>
                              </Box>
                            )}
                          </SimpleGrid>

                          <Divider my="14px" borderColor="portal.line" />

                          <Box>
                            <FieldLabel>{t("pilotNeeds.detail.sections.description")}</FieldLabel>
                            <Text
                              fontSize="13px"
                              color="portal.text"
                              whiteSpace="pre-wrap"
                              sx={{ marginBottom: "0 !important" }}
                            >
                              {need.description ||
                                t("pilotNeeds.detail.sections.noDescription")}
                            </Text>
                          </Box>

                          {(skillsTechnical.length > 0 ||
                            skillsFunctional.length > 0 ||
                            skillsBusiness.length > 0) && (
                            <>
                              <Divider my="14px" borderColor="portal.line" />
                              {skillsTechnical.length > 0 && (
                                <Box mb="12px">
                                  <FieldLabel>
                                    {t("pilotNeeds.detail.sections.technicalSkills")}
                                  </FieldLabel>
                                  <HStack wrap="wrap" spacing="6px">
                                    {skillsTechnical.map((skill, idx) => (
                                      <Badge
                                        key={`tech-${idx}`}
                                        colorScheme="blue"
                                        variant="subtle"
                                        fontSize="11px"
                                        px="8px"
                                        py="2px"
                                        borderRadius="999px"
                                      >
                                        {skill}
                                      </Badge>
                                    ))}
                                  </HStack>
                                </Box>
                              )}
                              {skillsFunctional.length > 0 && (
                                <Box mb="12px">
                                  <FieldLabel>
                                    {t("pilotNeeds.detail.sections.functionalSkills")}
                                  </FieldLabel>
                                  <HStack wrap="wrap" spacing="6px">
                                    {skillsFunctional.map((skill, idx) => (
                                      <Badge
                                        key={`func-${idx}`}
                                        colorScheme="orange"
                                        variant="subtle"
                                        fontSize="11px"
                                        px="8px"
                                        py="2px"
                                        borderRadius="999px"
                                      >
                                        {skill}
                                      </Badge>
                                    ))}
                                  </HStack>
                                </Box>
                              )}
                              {skillsBusiness.length > 0 && (
                                <Box mb="12px">
                                  <FieldLabel>
                                    {t("pilotNeeds.detail.sections.businessSkills")}
                                  </FieldLabel>
                                  <HStack wrap="wrap" spacing="6px">
                                    {skillsBusiness.map((skill, idx) => (
                                      <Badge
                                        key={`biz-${idx}`}
                                        colorScheme="green"
                                        variant="subtle"
                                        fontSize="11px"
                                        px="8px"
                                        py="2px"
                                        borderRadius="999px"
                                      >
                                        {skill}
                                      </Badge>
                                    ))}
                                  </HStack>
                                </Box>
                              )}
                            </>
                          )}

                          <Divider my="14px" borderColor="portal.line" />

                          <VStack align="flex-start" spacing="2px">
                            <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                              {t("pilotNeeds.detail.sections.createdAt")}:{" "}
                              {need.created_at ? new Date(need.created_at).toLocaleString() : "N/A"}
                            </Text>
                            <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                              {t("pilotNeeds.detail.sections.updatedAt")}:{" "}
                              {need.updated_at ? new Date(need.updated_at).toLocaleString() : "N/A"}
                            </Text>
                          </VStack>
                        </Box>
                      </Panel>
                    </VStack>
                  )}
                </TabPanel>

                {/* Tab 2: Matches */}
                <TabPanel px={0} pt="14px">
                  <Panel
                    title={t("pilotMatches.detail.matchesForNeedTitle", {
                      title: need?.title || ""
                    })}
                    count={loading ? undefined : sortedMatches.length}
                    actions={
                      <Button
                        size="sm"
                        leftIcon={<SearchIcon />}
                        variant="primary"
                        isLoading={generatingMatches}
                        isDisabled={!need || generatingMatches}
                        onClick={openSelectConsultants}
                      >
                        {matches.length > 0
                          ? t("pilotMatches.list.actions.regenerateMatches")
                          : t("pilotMatches.list.actions.generateMatches")}
                      </Button>
                    }
                  >
                    {!loading && !need ? (
                      <EmptyState title={t("pilotNeeds.detail.notFound")} />
                    ) : (
                      <DataTable
                        columns={matchColumns}
                        rows={sortedMatches}
                        loading={loading}
                        emptyState={matchesEmpty}
                        renderRow={(m) => (
                          <Tr
                            key={m.id}
                            cursor="pointer"
                            onClick={() => handleOpenMatchDetail(m, need)}
                          >
                            <Td fontWeight="700">{getConsultantDisplayName(m)}</Td>
                            <Td>{m.score != null ? m.score : "-"}</Td>
                            <Td>{m.source || "-"}</Td>
                            <Td title={m.reasoning || ""}>{m.reasoning || "-"}</Td>
                            <Td textAlign="right">
                              <MiniIconButton
                                aria-label={t("pilotMatches.detail.actions.delete")}
                                icon={<DeleteIcon />}
                                color="portal.danger"
                                _hover={{
                                  bg: "danger.50",
                                  borderColor: "portal.danger",
                                  color: "portal.danger"
                                }}
                                isDisabled={!canDelete}
                                onClick={(e) => handleDeleteMatch(m.id, e)}
                              />
                            </Td>
                          </Tr>
                        )}
                      />
                    )}
                  </Panel>
                </TabPanel>

                {/* Tab 3: Pairings (Consultants) */}
                <TabPanel px={0} pt="14px">
                  <Panel
                    title={t("pilotNeeds.detail.pairings.title")}
                    count={loading ? undefined : sortedMatches.length}
                    actions={
                      <Button
                        size="sm"
                        leftIcon={<AddIcon />}
                        variant="primary"
                        onClick={openAddConsultants}
                        isDisabled={!need || creatingPairings}
                        isLoading={creatingPairings}
                      >
                        {t("pilotNeeds.detail.pairings.actions.add")}
                      </Button>
                    }
                  >
                    {!loading && !need ? (
                      <EmptyState title={t("pilotNeeds.detail.notFound")} />
                    ) : (
                      <DataTable
                        columns={pairingColumns}
                        rows={sortedMatches}
                        loading={loading}
                        emptyState={matchesEmpty}
                        renderRow={(m) => {
                          const assignmentStatus =
                            m.assignment_status || m.assignmentStatus || "";
                          return (
                            <Tr
                              key={m.id}
                              cursor="pointer"
                              onClick={() => handleOpenMatchDetail(m, need)}
                            >
                              <Td fontWeight="700">{getConsultantDisplayName(m)}</Td>
                              <Td>
                                {assignmentStatus ? (
                                  <StatusBadge
                                    label={getStatusLabel(
                                      t,
                                      MATCH_ASSIGNMENT_STATUS_LABEL_KEYS,
                                      MATCH_ASSIGNMENT_STATUS_LABELS,
                                      assignmentStatus
                                    )}
                                    colorScheme="blue"
                                  />
                                ) : (
                                  "-"
                                )}
                              </Td>
                              <Td>{m.score != null ? m.score : "-"}</Td>
                              <Td>{m.source || "-"}</Td>
                              <Td textAlign="right">
                                <MiniIconButton
                                  aria-label={t("pilotMatches.detail.actions.delete")}
                                  icon={<DeleteIcon />}
                                  color="portal.danger"
                                  _hover={{
                                    bg: "danger.50",
                                    borderColor: "portal.danger",
                                    color: "portal.danger"
                                  }}
                                  isDisabled={!canDelete}
                                  onClick={(e) => handleDeleteMatch(m.id, e)}
                                />
                              </Td>
                            </Tr>
                          );
                        }}
                      />
                    )}
                  </Panel>
                </TabPanel>

                {/* Tab 4: Comments */}
                <TabPanel px={0} pt="14px">
                  <Panel title={t("pilotNeeds.detail.comments.title")}>
                    <Box p="12px">
                      <ThreadedComments
                        comments={comments}
                        isLoading={loadingComments}
                        emptyLabel={t("pilotNeeds.detail.comments.noComments")}
                        submitLabel={t("pilotNeeds.detail.comments.actions.submit")}
                        replyLabel={t("pilotNeeds.detail.comments.actions.reply")}
                        cancelLabel={t("pilotNeeds.detail.comments.actions.cancel")}
                        placeholder={t("pilotNeeds.detail.comments.placeholders.new")}
                        replyPlaceholder={t("pilotNeeds.detail.comments.placeholders.reply")}
                        onCreate={async ({ text, parentCommentId }) => {
                          await addNeedComment(
                            needId,
                            text,
                            { buId: selectedBusinessUnitId || undefined },
                            parentCommentId
                          );
                        }}
                        mentionableUsers={mentionableUsers}
                        onReload={fetchComments}
                        onCreateSuccess={() =>
                          toast({
                            title: t("pilotNeeds.detail.toasts.commentAdded"),
                            status: "success"
                          })
                        }
                        onCreateError={(e) =>
                          toast({
                            title: t("pilotNeeds.detail.toasts.commentFailedTitle"),
                            description:
                              e?.response?.data?.message ||
                              t("pilotNeeds.detail.toasts.commentFailedDescription"),
                            status: "error"
                          })
                        }
                      />
                    </Box>
                  </Panel>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={safeClose}>
              {t("pilotNeeds.detail.close")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Generate matches: select consultants */}
      <Modal isOpen={isSelectConsultantsOpen} onClose={closeSelectConsultants} size="2xl">
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            {t("pilotMatches.list.selectModal.title")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="16px">
            <RadioGroup value={generationMode} onChange={(value) => setGenerationMode(value)}>
              <Flex direction="column" gap="4px" mb="14px">
                <Radio value="auto" colorScheme="blue">
                  <Text fontSize="13px" sx={{ marginBottom: "0 !important" }}>
                    {t("pilotMatches.list.selectModal.modeAuto")}
                  </Text>
                </Radio>
                <Radio value="manual" colorScheme="blue">
                  <Text fontSize="13px" sx={{ marginBottom: "0 !important" }}>
                    {t("pilotMatches.list.selectModal.modeManual")}
                  </Text>
                </Radio>
              </Flex>
            </RadioGroup>

            {generationMode === "manual" && (
              <>
                <HStack mb="12px" justify="space-between" align="center">
                  <FilterSearch
                    placeholder={t("pilotMatches.list.selectModal.searchPlaceholder")}
                    value={consultantsSearch}
                    onChange={(e) => {
                      setConsultantsSearch(e.target.value);
                      setConsultantsPage(1);
                    }}
                    containerProps={{ maxW: "260px", flex: "0 0 auto" }}
                  />
                  <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                    {t("pilotMatches.list.selectModal.selectedCount", {
                      count: selectedConsultantIds.length,
                      max: 5
                    })}
                  </Text>
                </HStack>

                <Box
                  borderWidth="1px"
                  borderColor="portal.line"
                  borderRadius="10px"
                  overflow="hidden"
                  maxH="320px"
                  overflowY="auto"
                >
                  <DataTable
                    columns={selectConsultantColumns}
                    rows={consultants}
                    loading={consultantsLoading}
                    emptyState={
                      <EmptyState
                        icon={<FaUserFriends />}
                        title={t("pilotMatches.list.selectModal.noConsultants")}
                      />
                    }
                    renderRow={(c) => {
                      const id = c.id;
                      const isSelected = selectedConsultantIds.includes(id);
                      return (
                        <Tr
                          key={id}
                          cursor="pointer"
                          onClick={() => toggleConsultantSelection(id)}
                        >
                          <Td>
                            <Checkbox
                              isChecked={isSelected}
                              onChange={() => toggleConsultantSelection(id)}
                              pointerEvents="none"
                              colorScheme="blue"
                            />
                          </Td>
                          <Td fontWeight="700">{c.name || c.fullName || c.full_name}</Td>
                          <Td>{c.title || "-"}</Td>
                        </Tr>
                      );
                    }}
                  />
                </Box>

                <Flex justify="space-between" align="center" mt="12px">
                  <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                    {t("pilotMatches.list.selectModal.total", { count: consultantsTotalItems })}
                  </Text>
                  <HStack spacing="8px">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setConsultantsPage((p) => Math.max(1, p - 1))}
                      isDisabled={consultantsPage <= 1}
                    >
                      {t("pilotMatches.list.pagination.prev")}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setConsultantsPage((p) => p + 1)}
                      isDisabled={consultants.length < consultantsPageSize}
                    >
                      {t("pilotMatches.list.pagination.next")}
                    </Button>
                  </HStack>
                </Flex>
              </>
            )}
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={closeSelectConsultants}>
              {t("pilotMatches.list.selectModal.cancelButton")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleConfirmGenerate}
              isDisabled={generationMode === "manual" && selectedConsultantIds.length === 0}
              isLoading={generatingMatches}
            >
              {t("pilotMatches.list.selectModal.primaryButton")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add existing consultants as pairings */}
      <Modal isOpen={isAddConsultantsOpen} onClose={closeAddConsultants} size="2xl">
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            {t("pilotNeeds.detail.pairings.modal.title")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="16px">
            <HStack mb="12px" justify="space-between" align="center">
              <FilterSearch
                placeholder={t("pilotNeeds.detail.pairings.modal.searchPlaceholder")}
                value={pairingConsultantsSearch}
                onChange={(e) => {
                  setPairingConsultantsSearch(e.target.value);
                  setPairingConsultantsPage(1);
                }}
                containerProps={{ maxW: "260px", flex: "0 0 auto" }}
              />
              <Text fontSize="12px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                {t("pilotNeeds.detail.pairings.modal.selectedCount", {
                  count: selectedPairingConsultantIds.length,
                  max: 5
                })}
              </Text>
            </HStack>

            <Box
              borderWidth="1px"
              borderColor="portal.line"
              borderRadius="10px"
              overflow="hidden"
              maxH="320px"
              overflowY="auto"
            >
              <DataTable
                columns={pairingSelectColumns}
                rows={pairingConsultants}
                loading={pairingConsultantsLoading}
                emptyState={
                  <EmptyState
                    icon={<FaUserFriends />}
                    title={t("pilotNeeds.detail.pairings.modal.noConsultants")}
                  />
                }
                renderRow={(c) => {
                  const id = c.id;
                  const isSelected = selectedPairingConsultantIds.includes(id);
                  return (
                    <Tr
                      key={id}
                      cursor="pointer"
                      onClick={() => togglePairingConsultantSelection(id)}
                    >
                      <Td>
                        <Checkbox
                          isChecked={isSelected}
                          onChange={() => togglePairingConsultantSelection(id)}
                          pointerEvents="none"
                          colorScheme="blue"
                        />
                      </Td>
                      <Td fontWeight="700">{c.name || c.fullName || c.full_name}</Td>
                      <Td>{c.title || "-"}</Td>
                    </Tr>
                  );
                }}
              />
            </Box>

            <Flex justify="space-between" align="center" mt="12px">
              <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                {t("pilotNeeds.detail.pairings.modal.total", {
                  count: pairingConsultantsTotalItems
                })}
              </Text>
              <HStack spacing="8px">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPairingConsultantsPage((p) => Math.max(1, p - 1))}
                  isDisabled={pairingConsultantsPage <= 1}
                >
                  {t("pilotMatches.list.pagination.prev")}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPairingConsultantsPage((p) => p + 1)}
                  isDisabled={pairingConsultants.length < pairingConsultantsPageSize}
                >
                  {t("pilotMatches.list.pagination.next")}
                </Button>
              </HStack>
            </Flex>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={closeAddConsultants}>
              {t("pilotNeeds.detail.pairings.modal.cancelButton")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleConfirmAddConsultants}
              isDisabled={selectedPairingConsultantIds.length === 0}
              isLoading={creatingPairings}
            >
              {t("pilotNeeds.detail.pairings.modal.primaryButton")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <NeedFormModal
        isOpen={editModal.isOpen}
        onClose={() => {
          setEditingItem(null);
          editModal.onClose();
        }}
        initialData={editingItem}
        onSuccess={handleEditSuccess}
      />
      <MatchDetailModal
        isOpen={matchDetailModal.isOpen}
        onClose={matchDetailModal.onClose}
        matchId={selectedMatchId}
        initialMatch={selectedMatch}
        initialNeed={selectedNeed}
      />
      <ConfirmDialog
        isOpen={deleteConfirmDialog.isOpen}
        onClose={deleteConfirmDialog.onClose}
        onConfirm={handleDelete}
        title={t("pilotNeeds.detail.confirmDeleteTitle", { defaultValue: "Delete need?" })}
        body={t("pilotNeeds.detail.confirmDelete")}
        confirmLabel={t("common.delete", { defaultValue: "Delete" })}
      />
    </>
  );
};

export default NeedDetailModal;
